import React, { useRef, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    listAdminContent,
    getAdminContent,
    updateAdminContent,
    uploadAdminPhoto,
} from "@/lib/api";
import { Loader2, Save, Upload, Image as ImageIcon, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const LINES_TO_ARRAY = (v) =>
    v
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
const ARRAY_TO_LINES = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");

const EMPTY_LEFT = {
    title: "",
    subtitle: "",
    image: "",
    price: "",
    devices: [],
    features: [],
};

const EMPTY_RIGHT = {
    title: "",
    subtitle: "",
    image: "",
    price: "",
    devices: [],
    features: [],
    examples: [],
};

const EMPTY_HERO = {
    eyebrow: "",
    banner: "",
    cta_catalog: "",
    cta_quote: "",
};

const Content = () => {
    const queryClient = useQueryClient();
    const [selectedKey, setSelectedKey] = useState("");
    const [uploadingField, setUploadingField] = useState(null);
    const fileInputRef = useRef(null);
    const [pendingFileTarget, setPendingFileTarget] = useState(null);

    // Form state for hero sections
    const [leftForm, setLeftForm] = useState(EMPTY_LEFT);
    const [rightForm, setRightForm] = useState(EMPTY_RIGHT);
    const [heroForm, setHeroForm] = useState(EMPTY_HERO);
    // Raw JSON for other sections
    const [json, setJson] = useState("{}");
    const [parseError, setParseError] = useState(null);

    const { data: sections = [], isLoading } = useQuery({
        queryKey: ["admin", "content"],
        queryFn: listAdminContent,
    });

    const { data: selectedSection, isLoading: loadingSection } = useQuery({
        queryKey: ["admin", "content", selectedKey],
        queryFn: () => getAdminContent(selectedKey),
        enabled: !!selectedKey,
    });

    useEffect(() => {
        if (!selectedSection) return;
        const data = selectedSection.data || {};
        if (selectedKey === "hero") {
            setHeroForm({
                eyebrow: data.eyebrow || "",
                banner: data.trust_lines?.[0]?.label || "",
                cta_catalog: data.cta_catalog || "",
                cta_quote: data.cta_quote || "",
            });
        } else if (selectedKey === "hero_left") {
            setLeftForm({
                title: data.title || "",
                subtitle: data.subtitle || "",
                image: data.image || "",
                price: data.price || "",
                devices: Array.isArray(data.devices) ? data.devices : [],
                features: Array.isArray(data.features) ? data.features : [],
            });
        } else if (selectedKey === "hero_right") {
            setRightForm({
                title: data.title || "",
                subtitle: data.subtitle || "",
                image: data.image || "",
                price: data.price || "",
                devices: Array.isArray(data.devices) ? data.devices : [],
                features: Array.isArray(data.features) ? data.features : [],
                examples: Array.isArray(data.examples) ? data.examples : [],
            });
        } else {
            setJson(JSON.stringify(data, null, 2));
        }
        setParseError(null);
    }, [selectedSection, selectedKey]);

    const mutation = useMutation({
        mutationFn: (data) => updateAdminContent(selectedKey, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "content"] });
            queryClient.invalidateQueries({ queryKey: ["content", selectedKey] });
            toast.success("Contenu enregistré.");
            setParseError(null);
        },
        onError: (err) => {
            toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement.");
        },
    });

    const buildHeroData = () => {
        if (selectedKey === "hero") {
            const current = selectedSection?.data || {};
            return {
                ...current,
                eyebrow: heroForm.eyebrow,
                cta_catalog: heroForm.cta_catalog,
                cta_quote: heroForm.cta_quote,
                trust_lines: [{ label: heroForm.banner, color: "blue" }],
            };
        }
        if (selectedKey === "hero_left") {
            return {
                ...leftForm,
                devices: LINES_TO_ARRAY(leftForm.devices.join("\n")),
                features: LINES_TO_ARRAY(leftForm.features.join("\n")),
            };
        }
        if (selectedKey === "hero_right") {
            return {
                ...rightForm,
                devices: LINES_TO_ARRAY(rightForm.devices.join("\n")),
                features: LINES_TO_ARRAY(rightForm.features.join("\n")),
                examples: rightForm.examples.filter(Boolean),
            };
        }
        return null;
    };

    const handleSave = () => {
        if (selectedKey === "hero" || selectedKey === "hero_left" || selectedKey === "hero_right") {
            mutation.mutate(buildHeroData());
            return;
        }
        try {
            const data = JSON.parse(json);
            setParseError(null);
            mutation.mutate(data);
        } catch (err) {
            setParseError("JSON invalide : " + err.message);
        }
    };

    const uploadFile = async (file) => {
        const res = await uploadAdminPhoto(file);
        const url = res.url || res.photo || res.image || "";
        if (!url) throw new Error("Aucune URL retournée");
        return url;
    };

    const handleFileSelect = (target) => {
        setPendingFileTarget(target);
        fileInputRef.current?.click();
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !pendingFileTarget) return;
        setUploadingField(pendingFileTarget);
        try {
            const url = await uploadFile(file);
            if (pendingFileTarget === "hero_left_image") {
                setLeftForm((f) => ({ ...f, image: url }));
            } else if (pendingFileTarget === "hero_right_image") {
                setRightForm((f) => ({ ...f, image: url }));
            } else if (pendingFileTarget === "hero_right_example") {
                setRightForm((f) => ({ ...f, examples: [...f.examples, url] }));
            }
            toast.success("Image uploadée : " + url);
        } catch (err) {
            toast.error(err.message || "Échec de l'upload");
        } finally {
            setUploadingField(null);
            setPendingFileTarget(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removeExample = (idx) => {
        setRightForm((f) => ({
            ...f,
            examples: f.examples.filter((_, i) => i !== idx),
        }));
    };

    const ImagePreview = ({ url, onRemove }) => {
        if (!url) return null;
        return (
            <div className="relative inline-block w-24 h-24 rounded-xl border border-neutral-200 overflow-hidden bg-white group">
                <img src={url} alt="preview" className="w-full h-full object-cover" />
                {onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                    >
                        <Trash2 className="w-3 h-3" />
                    </button>
                )}
            </div>
        );
    };

    const renderHero = () => (
        <div className="space-y-5">
            <div className="space-y-1.5">
                <Label>Texte au-dessus des panneaux (eyebrow)</Label>
                <Input
                    value={heroForm.eyebrow}
                    onChange={(e) => setHeroForm((f) => ({ ...f, eyebrow: e.target.value }))}
                    placeholder="Atelier AX · Accessoires Mobiles"
                />
            </div>
            <div className="space-y-1.5">
                <Label>Message de la bannière défilante</Label>
                <Textarea
                    dir="auto"
                    rows={3}
                    value={heroForm.banner}
                    onChange={(e) => setHeroForm((f) => ({ ...f, banner: e.target.value }))}
                    placeholder="🚚 توصيل مجاني ..."
                />
                <p className="text-xs text-neutral-500">
                    Ce texte défilera en haut de la page, à côté du bouton WhatsApp.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <Label>Bouton catalogue</Label>
                    <Input
                        value={heroForm.cta_catalog}
                        onChange={(e) => setHeroForm((f) => ({ ...f, cta_catalog: e.target.value }))}
                        placeholder="Découvrir le catalogue"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label>Bouton devis</Label>
                    <Input
                        value={heroForm.cta_quote}
                        onChange={(e) => setHeroForm((f) => ({ ...f, cta_quote: e.target.value }))}
                        placeholder="Demander un devis"
                    />
                </div>
            </div>
        </div>
    );

    const renderHeroLeft = () => (
        <div className="space-y-5">
            <div className="space-y-1.5">
                <Label>Titre (arabe ou autre)</Label>
                <Input
                    dir="auto"
                    value={leftForm.title}
                    onChange={(e) => setLeftForm((f) => ({ ...f, title: e.target.value }))}
                />
            </div>
            <div className="space-y-1.5">
                <Label>Sous-titre</Label>
                <Textarea
                    dir="auto"
                    rows={3}
                    value={leftForm.subtitle}
                    onChange={(e) => setLeftForm((f) => ({ ...f, subtitle: e.target.value }))}
                />
            </div>
            <div className="space-y-1.5">
                <Label>Image principale (PNG recommandé)</Label>
                <div className="flex flex-wrap items-center gap-3">
                    <ImagePreview url={leftForm.image} />
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleFileSelect("hero_left_image")}
                        disabled={uploadingField === "hero_left_image"}
                    >
                        {uploadingField === "hero_left_image" ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                            <Upload className="w-4 h-4 mr-2" />
                        )}
                        {leftForm.image ? "Changer l'image" : "Upload image"}
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <Label>Prix affiché</Label>
                    <Input
                        value={leftForm.price}
                        onChange={(e) => setLeftForm((f) => ({ ...f, price: e.target.value }))}
                        placeholder="50 DH"
                    />
                </div>
            </div>
            <div className="space-y-1.5">
                <Label>Appareils compatibles (un par ligne)</Label>
                <Textarea
                    dir="auto"
                    rows={4}
                    value={ARRAY_TO_LINES(leftForm.devices)}
                    onChange={(e) =>
                        setLeftForm((f) => ({ ...f, devices: LINES_TO_ARRAY(e.target.value) }))
                    }
                />
            </div>
            <div className="space-y-1.5">
                <Label>Avantages (un par ligne)</Label>
                <Textarea
                    dir="auto"
                    rows={4}
                    value={ARRAY_TO_LINES(leftForm.features)}
                    onChange={(e) =>
                        setLeftForm((f) => ({ ...f, features: LINES_TO_ARRAY(e.target.value) }))
                    }
                />
            </div>
        </div>
    );

    const renderHeroRight = () => (
        <div className="space-y-5">
            <div className="space-y-1.5">
                <Label>Titre (arabe ou autre)</Label>
                <Input
                    dir="auto"
                    value={rightForm.title}
                    onChange={(e) => setRightForm((f) => ({ ...f, title: e.target.value }))}
                />
            </div>
            <div className="space-y-1.5">
                <Label>Sous-titre</Label>
                <Textarea
                    dir="auto"
                    rows={3}
                    value={rightForm.subtitle}
                    onChange={(e) => setRightForm((f) => ({ ...f, subtitle: e.target.value }))}
                />
            </div>
            <div className="space-y-1.5">
                <Label>Image principale</Label>
                <div className="flex flex-wrap items-center gap-3">
                    <ImagePreview url={rightForm.image} />
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleFileSelect("hero_right_image")}
                        disabled={uploadingField === "hero_right_image"}
                    >
                        {uploadingField === "hero_right_image" ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                            <Upload className="w-4 h-4 mr-2" />
                        )}
                        {rightForm.image ? "Changer l'image" : "Upload image"}
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <Label>Prix affiché</Label>
                    <Input
                        value={rightForm.price}
                        onChange={(e) => setRightForm((f) => ({ ...f, price: e.target.value }))}
                        placeholder="60 DH"
                    />
                </div>
            </div>
            <div className="space-y-1.5">
                <Label>Appareils compatibles (un par ligne)</Label>
                <Textarea
                    dir="auto"
                    rows={4}
                    value={ARRAY_TO_LINES(rightForm.devices)}
                    onChange={(e) =>
                        setRightForm((f) => ({ ...f, devices: LINES_TO_ARRAY(e.target.value) }))
                    }
                />
            </div>
            <div className="space-y-1.5">
                <Label>Avantages (un par ligne)</Label>
                <Textarea
                    dir="auto"
                    rows={4}
                    value={ARRAY_TO_LINES(rightForm.features)}
                    onChange={(e) =>
                        setRightForm((f) => ({ ...f, features: LINES_TO_ARRAY(e.target.value) }))
                    }
                />
            </div>

            <div className="space-y-1.5">
                <Label>Exemples d’impressions</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                    {rightForm.examples.map((url, idx) => (
                        <ImagePreview key={idx} url={url} onRemove={() => removeExample(idx)} />
                    ))}
                </div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleFileSelect("hero_right_example")}
                    disabled={uploadingField === "hero_right_example"}
                >
                    {uploadingField === "hero_right_example" ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                        <Plus className="w-4 h-4 mr-2" />
                    )}
                    Ajouter un exemple
                </Button>
            </div>
        </div>
    );

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight">Contenu</h1>
                    <p className="text-neutral-500 mt-1">
                        Éditez les sections dynamiques du site.
                    </p>
                </div>

                <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    ref={fileInputRef}
                    onChange={handleUpload}
                    className="hidden"
                />

                <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4">
                    <div className="space-y-1.5">
                        <Label>Section</Label>
                        <Select value={selectedKey} onValueChange={setSelectedKey}>
                            <SelectTrigger>
                                <SelectValue placeholder="Choisir une section" />
                            </SelectTrigger>
                            <SelectContent>
                                {sections.map((s) => (
                                    <SelectItem key={s.key} value={s.key}>
                                        {s.key}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedKey && loadingSection && (
                        <div className="py-12 flex justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
                        </div>
                    )}

                    {selectedKey && !loadingSection && (
                        <>
                            {selectedKey === "hero" && renderHero()}
                            {selectedKey === "hero_left" && renderHeroLeft()}
                            {selectedKey === "hero_right" && renderHeroRight()}
                            {selectedKey !== "hero" && selectedKey !== "hero_left" && selectedKey !== "hero_right" && (
                                <div className="space-y-1.5">
                                    <Label>Données JSON</Label>
                                    <Textarea
                                        value={json}
                                        onChange={(e) => setJson(e.target.value)}
                                        rows={20}
                                        className="font-mono text-sm"
                                    />
                                    {parseError && (
                                        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                            {parseError}
                                        </div>
                                    )}
                                </div>
                            )}

                            <Button
                                onClick={handleSave}
                                disabled={mutation.isPending}
                                className="bg-[#1C1C1E] hover:bg-black rounded-full"
                            >
                                {mutation.isPending && (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                )}
                                <Save className="w-4 h-4 mr-2" />
                                Enregistrer
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default Content;
